const monthByAbbreviation: Record<string, number> = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12,
}

function formatCalendarDate(year: number, month: number, day: number, original: string) {
  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const daysByMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  if (month < 1 || month > 12 || day < 1 || day > daysByMonth[month - 1]) {
    throw new Error(`Invalid date: ${original}`)
  }

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function parseCalendarDateParts(original: string) {
  const normalized = original.trim()
  const yearFirstMatch = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[T\s])/)
  if (yearFirstMatch) {
    return {
      year: Number(yearFirstMatch[1]),
      month: Number(yearFirstMatch[2]),
      day: Number(yearFirstMatch[3]),
    }
  }

  const dayFirstMatch = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:$|[T\s])/)
  if (dayFirstMatch) {
    return {
      year: Number(dayFirstMatch[3]),
      month: Number(dayFirstMatch[2]),
      day: Number(dayFirstMatch[1]),
    }
  }

  const legacyMatch = normalized.match(
    /^(?:Sun|Mon|Tue|Wed|Thu|Fri|Sat)\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+(\d{4})(?:$|\s)/,
  )
  if (legacyMatch) {
    return {
      year: Number(legacyMatch[3]),
      month: monthByAbbreviation[legacyMatch[1]],
      day: Number(legacyMatch[2]),
    }
  }

  throw new Error(`Invalid date: ${original}`)
}

/** Preserva a data civil informada no CSV, sem conversão de fuso horário. */
export const parseCsvCalendarDate = (original?: string): string | null => {
  if (!original?.trim()) return null
  const { year, month, day } = parseCalendarDateParts(original)
  return formatCalendarDate(year, month, day, original)
}

/** Preserva a data e hora locais informadas no CSV, ignorando sufixos de fuso legados. */
export const parseCsvLocalDateTime = (original?: string): string | null => {
  if (!original?.trim()) return null
  const normalized = original.trim()
  const { year, month, day } = parseCalendarDateParts(normalized)
  const calendarDate = formatCalendarDate(year, month, day, original)

  const yearFirstTimeMatch = normalized.match(
    /^\d{4}-\d{1,2}-\d{1,2}[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?/,
  )
  const dayFirstTimeMatch = normalized.match(
    /^\d{1,2}\/\d{1,2}\/\d{4}[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?/,
  )
  const legacyTimeMatch = normalized.match(
    /^(?:Sun|Mon|Tue|Wed|Thu|Fri|Sat)\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}\s+\d{4}\s+(\d{1,2}):(\d{2})(?::(\d{2}))?/,
  )
  const timeMatch = yearFirstTimeMatch ?? dayFirstTimeMatch ?? legacyTimeMatch
  const hour = timeMatch ? Number(timeMatch[1]) : 0
  const minute = timeMatch ? Number(timeMatch[2]) : 0
  const second = timeMatch?.[3] ? Number(timeMatch[3]) : 0

  if (hour > 23 || minute > 59 || second > 59) {
    throw new Error(`Invalid date: ${original}`)
  }

  return `${calendarDate} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`
}

/**
 * Converte uma string CSV para valor booleano
 * @param value - String contendo '1' para true, qualquer outro valor para false
 * @returns Boolean
 * @example
 * parseBooleanValue('1') // returns true
 * parseBooleanValue('0') // returns false
 * parseBooleanValue('') // returns false
 */
export const parseBooleanValue = (value?: string): boolean => {
  return value === '1'
}

/**
 * Converte booleanos CSV sem confundir ausência com valor falso.
 * Aceita as representações produzidas pelos extratores atuais.
 */
export const parseNullableBooleanValue = (value?: string): boolean | null => {
  const normalizedValue = value?.trim().toLowerCase()

  if (!normalizedValue) return null
  if (normalizedValue === '1' || normalizedValue === 'true') return true
  if (normalizedValue === '0' || normalizedValue === 'false') return false

  throw new Error(`Invalid nullable boolean: ${value}`)
}

/**
 * Converte uma string CSV para string ou null se vazia
 * @param value - String do CSV
 * @returns String ou null se vazio
 * @example
 * parseEnumEmptyString('value') // returns 'value'
 * parseEnumEmptyString('') // returns null
 * parseEnumEmptyString(null) // returns null
 */
export const parseEnumEmptyString = (value?: string): string | null => {
  return value ? value : null
}

/**
 * Converte uma string CSV separada por vírgulas em array de strings
 * @param value - String contendo valores separados por vírgula
 * @returns Array de strings, removendo espaços e valores vazios
 * @example
 * parseStringArray('a,b,c') // returns ['a', 'b', 'c']
 * parseStringArray('a, b , c') // returns ['a', 'b', 'c']
 * parseStringArray('a,,c') // returns ['a', 'c']
 * parseStringArray('') // returns []
 * parseStringArray(null) // returns []
 */
export const parseStringArray = (value?: string): string[] => {
  if (!value || value.trim() === '') {
    return []
  }
  return value
    .split(',')
    .map(item => item.trim())
    .filter(item => item !== '')
}
